import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationCircle, 
  faCheckCircle, 
  faExclamationTriangle, 
  faInfoCircle,
  faTimes 
} from '@fortawesome/free-solid-svg-icons';
import './ErrorMessage.css';

/**
 * Reusable Message Component for errors, success, warnings, and info
 */
const Message = ({
  type = 'error',
  message,
  dismissible = false,
  autoHide = false,
  autoHideDelay = 3000,
  onDismiss,
  compact = false,
  showIcon = true,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsFading(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onDismiss) {
        onDismiss();
      }
    }, 300);
  }, [onDismiss]);

  useEffect(() => {
    if (autoHide && message) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
  }, [message, autoHide, autoHideDelay, handleDismiss]);

  const getMessageClasses = () => {
    const baseClass = `${type}-message`;
    const compactClass = compact ? 'message-compact' : '';
    const dismissibleClass = dismissible ? 'message-dismissible' : '';
    const noIconClass = !showIcon ? 'message-no-icon' : '';
    const fadeClass = isFading ? 'fade-out' : '';
    
    return [baseClass, compactClass, dismissibleClass, noIconClass, fadeClass, className]
      .filter(Boolean)
      .join(' ');
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return faCheckCircle;
      case 'warning':
        return faExclamationTriangle;
      case 'info':
        return faInfoCircle;
      case 'error':
      default:
        return faExclamationCircle;
    }
  };

  const getIconClass = () => {
    return `${type}-message-icon`;
  };

  if (!message || !isVisible) {
    return null;
  }

  return (
    <div className={getMessageClasses()}>
      {showIcon && <FontAwesomeIcon icon={getIcon()} className={getIconClass()} />}
      <span>{message}</span>
      {dismissible && (
        <button 
          className="message-close" 
          onClick={handleDismiss}
          aria-label="Dismiss message"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
};

// Convenience components for specific message types
export const ErrorMessage = (props) => <Message type="error" {...props} />;
export const SuccessMessage = (props) => <Message type="success" {...props} />;
export const WarningMessage = (props) => <Message type="warning" {...props} />;
export const InfoMessage = (props) => <Message type="info" {...props} />;

export default Message;
